import {useState, useEffect} from 'react';
import styled from 'styled-components';
import {useLocation} from 'react-router-dom';
import {useSearchParams, useNavigate} from '../../../router/compat';
import {useDebounce} from '../hooks/useDebounce';
import {forecastFetchQuery} from '../../../graphql/forecastRelay';
import {TicketLookupBySlugQuery, TicketLookupByIdQuery} from '../hooks/useTicketDetail';

const SearchBox = styled.div`
	display: inline-flex;
	height: 36px;
	width: 100%;
	align-items: center;
	gap: 8px;
	border-radius: 6px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 0 12px;
	@media (min-width: 640px) {
		width: auto;
		min-width: 200px;
	}
`;

const SearchIcon = styled.svg`
	height: 16px;
	width: 16px;
	flex-shrink: 0;
	color: #94a3b8;
`;

const SearchInput = styled.input`
	width: 100%;
	border: 0;
	background: transparent;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #334155;
	&::placeholder {
		color: #94a3b8;
	}
	&:focus {
		outline: none;
	}
`;

// Mirrors TicketDetailRoute's slug split: `ACME-1042` is a slug, anything else
// (e.g. a raw ticket id) is looked up by id.
const SLUG_PATTERN = /^[A-Z][A-Z0-9]{1,9}-\d+$/;

export function GlobalSearch() {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();

	const [inputValue, setInputValue] = useState(() => searchParams.get('search') ?? '');
	const debouncedValue = useDebounce(inputValue, 200);

	// Sync input from URL when navigating back/forward
	useEffect(() => {
		const urlSearch = searchParams.get('search') ?? '';
		setInputValue(urlSearch);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.key]);

	useEffect(() => {
		if (debouncedValue === '') {
			if (location.pathname === '/tickets') {
				setSearchParams(
					prev => {
						const updated = new URLSearchParams(prev);
						updated.delete('search');
						return updated;
					},
					{replace: true}
				);
			}
			return;
		}

		// #<value> prefix — attempt a direct ticket lookup (slug or id).
		if (debouncedValue.startsWith('#')) {
			const value = debouncedValue.slice(1);
			void (async () => {
				try {
					const isSlug = SLUG_PATTERN.test(value);
					const data = (await forecastFetchQuery(
						isSlug ? TicketLookupBySlugQuery : TicketLookupByIdQuery,
						isSlug ? {slug: value} : {id: value}
					)) as {
						viewer?: {
							ticketDetail?: {ticket?: unknown} | null;
							ticketDetailBySlug?: {ticket?: unknown} | null;
						} | null;
					} | null;
					const found = data?.viewer?.ticketDetail?.ticket ?? data?.viewer?.ticketDetailBySlug?.ticket ?? null;
					if (found) {
						navigate(`/tickets/${value}`);
						return;
					}
				} catch {
					// fall through to search
				}
				applySearch(debouncedValue);
			})();
			return;
		}

		applySearch(debouncedValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	function applySearch(value: string) {
		if (location.pathname === '/tickets') {
			setSearchParams(
				prev => {
					const updated = new URLSearchParams(prev);
					updated.set('search', value);
					return updated;
				},
				{replace: true}
			);
		} else {
			navigate(`/tickets?search=${encodeURIComponent(value)}`);
		}
	}

	return (
		<SearchBox>
			<SearchIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
				<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
			</SearchIcon>
			<SearchInput
				type="text"
				placeholder="Search tickets..."
				value={inputValue}
				onChange={e => setInputValue(e.target.value)}
			/>
		</SearchBox>
	);
}
